import { ColumnDefResolved } from "@tanstack/react-table";
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { DataGridInner } from "./DataGridInner";
import { DataGridProvider } from "./DataGridProvider";

export type DataTableSpacing = "xs" | "sm" | "lg";

export type DataTableSelectedRowIds<TData> = Set<TData>;

export interface DataTableColumn<TData> extends ColumnDefResolved<TData> {
  align?: "left" | "center" | "right";
  headerFontSize?: number;
  headerFontWeight?: number;
  headerColor?: string;
}

export type DataTableRequestParams = {
  pageIndex: number;
  pageSize: number;
  sorting?: { id: string; desc?: boolean }[];
  filters?: { id: string; value: unknown }[];
};

export interface DataTableProps<TData extends object> {
  title?: string;
  searchPlaceholder?: string;
  enableSearch?: boolean;
  enablePagination?: boolean;
  showHeadRow?: boolean;
  loading?: boolean;
  isInfoIcon?: boolean;
  header?: ReactNode;
  titleFontSize?: "sm" | "md" | "lg" | "xl";
  footer?: ReactNode;
  columns: ColumnDefResolved<TData>[];
  data?: TData[];
  rowSelect?: boolean;
  onRowClick?: (row: TData) => void;
  onRowsSelectChange?: (selectedRows: TData[]) => void;
  defaultSelectedRows?: TData[];
  messages?: {
    loading?: ReactNode | string;
    empty?: ReactNode | string;
  };
  layout?: {
    cellsBorder?: boolean;
    tableSpacing?: DataTableSpacing;
  };
  pagination?: {
    page?: number;
    size?: number;
    sizes?: number[];
    sizesInfo?: string;
    sizesLabel?: string;
    sizesDescription?: string;
    more?: boolean;
    moreLimit?: number;
    info?: string;
  };
  sorting?: { id: string; desc?: boolean }[];
  filters?: { id: string; value: unknown }[];
}

export const DataTable = <TData extends object>({
  title,
  searchPlaceholder,
  enableSearch,
  enablePagination,
  showHeadRow = true,
  header,
  footer,
  loading,
  isInfoIcon = true,
  onRowClick,
  titleFontSize = "lg",
  defaultSelectedRows,
  ...props
}: DataTableProps<TData>) => {
  const { t } = useTranslation();

  return (
    <div className="card card-grid min-w-full" key={props.data?.length}>
      {loading ? (
        <>
          <div className="card-header py-5 flex-wrap justify-between">
            <div className="flex flex-row gap-10 items-center">
              {title && (
                <h3 className={`card-title text-${titleFontSize}`}>{title}</h3>
              )}
              <div className="flex flex-row gap-10">
                {enableSearch && (
                  <div className="input h-8">
                    <i className="ki-filled ki-magnifier"></i>
                    <input
                      className="min-w-60"
                      placeholder={searchPlaceholder}
                      type="text"
                    />
                  </div>
                )}
                {isInfoIcon && (
                  <i className="ki-outline ki-information-3 text-[#8D4F61] text-1.5xl" />
                )}
              </div>
            </div>

            {header}
          </div>

          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-4 border-blue-500 border-t-transparent bg-white"></div>
          </div>
        </>
      ) : (
        <DataGridProvider
          defaultSelectedRows={defaultSelectedRows}
          enablePagination={enablePagination}
          {...props}
          messages={{
            loading: t("common.Loading"),
            empty: t("common.No Data Available"),
          }}
        >
          <DataGridInner
            onRowClick={onRowClick}
            columns={props.columns}
            title={title}
            searchPlaceholder={searchPlaceholder}
            enableSearch={enableSearch}
            enablePagination={enablePagination}
            header={header}
            footer={footer}
            showHeadRow={showHeadRow}
            isInfoIcon={isInfoIcon}
            titleFontSize={titleFontSize}
            defaultSelectedRows={defaultSelectedRows}
          />
        </DataGridProvider>
      )}
    </div>
  );
};
